import React, {
  useState,
  useEffect,
  useReducer,
  useContext
} from "react";
import {
  toast
} from "react-toastify";
import {
  makeStyles
} from "@material-ui/core/styles";
import {
  Paper,
  Button,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
  Chip,
  Avatar
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import LabelIcon from "@material-ui/icons/Label";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import api from "../../services/api";
import {
  i18n
} from "../../translate/i18n";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import {
  AuthContext
} from "../../context/Auth/AuthContext";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":     return [...state, ...action.payload];
    case "UPDATE_TAGS": {
      const tag = action.payload;
      const idx = state.findIndex(s => s.id === tag.id);
      if (idx !== -1) { state[idx] = tag; return [...state]; }
      return [tag, ...state];
    }
    case "DELETE_TAGS":   return state.filter(t => t.id !== action.payload);
    case "RESET":         return [];
    default:              return state;
  }
};

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: "1 1 auto",
    minHeight: "100%",
    paddingBottom: theme.spacing(8),
    gap: theme.spacing(1.5),
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",

    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
        : "linear-gradient(145deg, #EBF3FF 0%, #F0F6FF 100%)"
  },

  // ── HERO ──────────────────────────────────────────────────────────────────
  hero: {
    flexShrink: 0,
    borderRadius: 28,
    padding: theme.spacing(2.6),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #071B4D 0%, #0D1F3C 100%)"
      : "linear-gradient(135deg, #FFFFFF 0%, #EAF3FF 55%, #F8FAFC 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(0,140,255,0.18)" : "1px solid rgba(0,102,255,0.12)",
    boxShadow: "0 24px 58px rgba(0,102,255,0.12)",
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    alignItems: "center",
    gap: theme.spacing(2),
    position: "relative",
    overflow: "visible",
    boxSizing: "border-box",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    },
    [theme.breakpoints.down("sm")]: {
      borderRadius: 22,
      padding: theme.spacing(1.8)
    }
  },
  heroBgCircle: {
    position: "absolute", right: -20, top: -20, width: 140, height: 140,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    zIndex: 1,
    minWidth: 0
  },
  heroIcon: {
    width: 62, height: 62, borderRadius: 20, flexShrink: 0, fontSize: "1.7rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 16px rgba(0,102,255,0.3)",
  },
  heroTitle: {
    fontWeight: 950, fontSize: "1.85rem", letterSpacing: "-1px", lineHeight: 1.06,
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  heroSubtitle: {
    fontSize: "0.9rem", marginTop: 8, lineHeight: 1.48, maxWidth: 620,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.55)",
  },
  heroRight: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    }
  },
  heroChip: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "#FFFFFF",
    border: "1px solid rgba(0,102,255,0.15)", borderRadius: 14,
    fontWeight: 700, fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#0A1628",
  },
  addBtn: {
    borderRadius: 14, fontWeight: 900, fontSize: "0.82rem", textTransform: "none",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff", padding: theme.spacing(1.1, 2.2),
    boxShadow: "0 4px 14px rgba(0,102,255,0.28)",
    "&:hover": { background: "linear-gradient(135deg, #0052CC 0%, #007ACC 100%)" },
  },

  // ── SEARCH ────────────────────────────────────────────────────────────────
  searchField: {
    flexShrink: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12, fontSize: "0.82rem",
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#ffffff",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0066FF" },
    },
  },

  // ── GRID ──────────────────────────────────────────────────────────────────
  grid: {
    flex: "0 0 auto",
    minHeight: 0,
    overflowY: "visible",
    overflowX: "hidden",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(1.5),
    alignContent: "start",
    ...theme.scrollbarStyles,
  },

  // ── TAG CARD ──────────────────────────────────────────────────────────────
  tagCard: {
    borderRadius: 24,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
    boxShadow: "0 14px 30px rgba(15,23,42,0.07)",
    padding: theme.spacing(1.6),
    display: "flex", flexDirection: "column", gap: theme.spacing(1.2),
    transition: "all 0.18s ease",
    cursor: "default",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(0,102,255,0.10)",
      border: "1px solid rgba(0,102,255,0.2)",
    },
  },
  tagCardTop: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  tagColorDot: {
    width: 44, height: 44, borderRadius: 15, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
  },
  tagActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(.8),
    marginTop: theme.spacing(1.2)
  },
  tagActionBtn: {
    width: "100%",
    height: 34,
    borderRadius: 12,
    padding: 0,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
    "&:hover": { background: "rgba(0,102,255,0.08)", borderColor: "rgba(0,102,255,0.3)" },
  },
  tagName: {
    fontWeight: 950, fontSize: "0.96rem",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  tagMeta: {
    display: "flex", alignItems: "center", gap: 6,
    fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.4)" : "rgba(10,22,40,0.45)",
  },
  tagStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    borderRadius: 999,
    padding: "4px 9px",
    background: "rgba(0,168,107,.10)",
    color: "#00A86B",
    border: "1px solid rgba(0,168,107,.18)",
    fontSize: ".68rem",
    fontWeight: 900
  },
  tagId: {
    display: "inline-flex", alignItems: "center",
    background: "rgba(0,102,255,0.08)", color: "#0066FF",
    border: "1px solid rgba(0,102,255,0.15)",
    borderRadius: 6, padding: "1px 7px",
    fontSize: "0.65rem", fontWeight: 700,
  },
  emptyState: {
    gridColumn: "1 / -1",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: theme.spacing(6), gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: "3rem", opacity: 0.3, marginBottom: theme.spacing(1),
  },
}));

const Tags = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [loading, setLoading]               = useState(false);
  const [pageNumber, setPageNumber]         = useState(1);
  const [hasMore, setHasMore]               = useState(false);
  const [selectedTag, setSelectedTag]       = useState(null);
  const [deletingTag, setDeletingTag]       = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam]       = useState("");
  const [tags, dispatch]                    = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen]     = useState(false);

  useEffect(() => {
    const fetchMoreTags = async () => {
      try {
        const { data } = await api.get("/tags/", { params: { searchParam, pageNumber, kanban: 0 } });
        dispatch({ type: "LOAD_TAGS", payload: data.tags });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) { toastError(err); }
    };
    if (pageNumber > 0) { setLoading(true); fetchMoreTags(); }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const oldBodyOverflowX = document.body.style.overflowX;
    const oldHtmlOverflowX = document.documentElement.style.overflowX;
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";

    return () => {
      document.body.style.overflowX = oldBodyOverflowX;
      document.documentElement.style.overflowX = oldHtmlOverflowX;
    };
  }, []);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      if (data.action === "delete")
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
    };
    socket.on("company" + user.companyId + "-tag", onCompanyTags);
    return () => socket.off("company" + user.companyId + "-tag", onCompanyTags);
  }, [socket, user.companyId]);

  const handleSearch = (e) => {
    setSearchParam(e.target.value.toLowerCase());
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight)
      setPageNumber(p => p + 1);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete("/tags/" + tagId);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  return (
    <KoraxPage className={classes.pageRoot}>
      <ConfirmationModal
        title={deletingTag && i18n.t("tags.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <TagModal
        open={tagModalOpen}
        onClose={() => { setSelectedTag(null); setTagModalOpen(false); }}
        tagId={selectedTag && selectedTag.id}
        kanban={0}
      />

      <KoraxPageHeader
        title="Etiquetas"
        subtitle="Organize contatos, oportunidades e automações através de segmentações inteligentes."
        actions={
          <Button
            variant="contained"
            className={classes.addBtn}
            onClick={() => {
              setSelectedTag(null);
              setTagModalOpen(true);
            }}
            startIcon={<AddIcon />}
          >
            Nova Etiqueta
          </Button>
        }
      />

      {/* BUSCA */}
      <TextField
        placeholder="Pesquisar etiquetas..."
        type="search"
        value={searchParam}
        onChange={handleSearch}
        variant="outlined"
        size="small"
        className={classes.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "#0066FF", fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* GRID DE ETIQUETAS */}
      <div className={classes.grid} onScroll={handleScroll}>
        {tags.length === 0 && !loading && (
          <div className={classes.emptyState}>
            <LabelIcon className={classes.emptyIcon} />
            <Typography style={{ fontWeight: 600, fontSize: "0.9rem" }}>
              Nenhuma etiqueta encontrada
            </Typography>
            <Typography style={{ fontSize: "0.78rem" }}>
              Crie sua primeira etiqueta clicando em "Nova Etiqueta"
            </Typography>
          </div>
        )}

        {tags.map((tag) => (
          <div key={tag.id} className={classes.tagCard}>
            <div className={classes.tagCardTop}>
              <div
                className={classes.tagColorDot}
                style={{ background: tag.color || "#0066FF" }}
              >
                <LabelIcon style={{ color: "#fff", fontSize: 18 }} />
              </div>
            </div>

            <Typography className={classes.tagName}>{tag.name}</Typography>

            <div className={classes.tagMeta}>
              <PeopleAltOutlinedIcon style={{ fontSize: 13 }} />
              <span>{tag?.contacts?.length || 0} contatos vinculados</span>
              <span style={{ flex: 1 }} />
              <span className={classes.tagStatusBadge}>● Ativa</span>
              <span className={classes.tagId}>#{tag.id}</span>
            </div>

            <div className={classes.tagActions}>
              <button
                className={classes.tagActionBtn}
                onClick={() => { setSelectedTag(tag); setTagModalOpen(true); }}
                style={{ color: "#0066FF", fontWeight: 900, cursor: "pointer" }}
              >
                Editar
              </button>
              <button
                className={classes.tagActionBtn}
                onClick={() => { setConfirmModalOpen(true); setDeletingTag(tag); }}
                style={{ color: "#EF4444", fontWeight: 900, cursor: "pointer", borderColor: "rgba(239,68,68,.22)" }}
              >
                Excluir
              </button>
            </div>
          </div>
        ))}

        {loading && [1,2,3,4,5,6].map(i => (
          <div key={i} className={classes.tagCard} style={{ opacity: 0.4 }}>
            <div style={{ width: "60%", height: 14, borderRadius: 6, background: "#e2e8f0", marginBottom: 8 }} />
            <div style={{ width: "40%", height: 10, borderRadius: 6, background: "#e2e8f0" }} />
          </div>
        ))}
      </div>
    </KoraxPage>
  );
};

export default Tags;
